import React from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, MapPin, Sparkles, ArrowRight } from "lucide-react";
import { getProduct, relatedTo, CATEGORY_LABEL } from "@/data/products";
import { ProductCard } from "@/components/ProductCard";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function ProductDetail() {
  const { id } = useParams();
  const product = getProduct(id);
  const [activeImg, setActiveImg] = React.useState(0);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-32 text-center">
        <p className="text-muted-foreground">Style not found.</p>
        <Link to="/shop" className="mt-4 inline-block bg-ink text-white px-6 py-3 text-xs font-bold uppercase tracking-widest">Back to collections</Link>
      </div>
    );
  }

  const isDenim = !!product.washes;
  const images = isDenim && product.cadRef ? [product.image, product.cadRef] : [product.image];
  const related = relatedTo(product);
  const genderPath = `/${product.gender.toLowerCase()}`;
  const catLabel = CATEGORY_LABEL[product.category];

  return (
    <div data-testid="product-detail">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-6">
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono uppercase tracking-wider">
          <Link to="/" className="hover:text-ink">Home</Link><ChevronRight className="w-3 h-3" />
          <Link to={genderPath} className="hover:text-ink">{product.gender}</Link><ChevronRight className="w-3 h-3" />
          <Link to={`/${product.category}`} className="hover:text-ink">{catLabel}</Link>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6 lg:py-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12">
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-[80px_1fr] gap-3">
          {images.length > 1 && (
            <div className="order-2 sm:order-1 flex sm:flex-col gap-3 overflow-x-auto no-scrollbar">
              {images.map((img, i) => (
                <button key={i} data-testid={`thumb-${i}`} onClick={() => setActiveImg(i)} className={`shrink-0 w-16 sm:w-full aspect-[3/4] overflow-hidden border-2 bg-sand flex items-center justify-center transition-colors ${activeImg === i ? "border-ink" : "border-transparent"}`}>
                  <img src={img} alt={`view ${i + 1}`} className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
          <div className={`${images.length > 1 ? "order-1 sm:order-2" : "sm:col-span-2"} bg-sand overflow-hidden aspect-[3/4] flex items-center justify-center`}>
            <img key={activeImg} src={images[activeImg]} alt={product.name} className="w-full h-full object-contain p-4 ar-fade" />
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-[12px] uppercase tracking-widest text-denim">{product.styleCode}</span>
              <span className="text-[10px] font-mono uppercase tracking-widest bg-offwhite border border-border px-2 py-0.5 text-muted-foreground">{product.garmentType}</span>
            </div>
            <h1 className="mt-2 font-display text-3xl lg:text-4xl font-bold uppercase tracking-tight leading-tight">{product.name}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{product.gender} · {catLabel}</p>

            {isDenim ? (
              <div className="mt-7">
                <p className="text-xs font-mono uppercase tracking-widest mb-2.5 text-muted-foreground">Available Washes ({product.washes.length})</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {product.washes.map((w) => (
                    <span key={w} data-testid={`wash-${w}`} className="text-[11px] font-mono uppercase tracking-wide border border-border px-2.5 py-1 text-ink">{w}</span>
                  ))}
                </div>
                <div className="border border-border bg-sand/40">
                  <img src={product.washStrip} alt="Wash swatches from the reference sheet" className="w-full h-auto" />
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">Fabric wash swatches as shown on the official AR / American Rag reference sheet.</p>
              </div>
            ) : (
              <div className="mt-7 space-y-3">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-4 h-4 text-denim mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Treatment</p>
                    <p className="text-sm font-medium text-ink">{product.treatment}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-4 text-denim mt-0.5 shrink-0 font-bold text-center leading-4">"</span>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Graphic</p>
                    <p className="text-sm font-medium text-ink">{product.graphic || "Graphic shown on garment — see image."}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-col gap-3">
              <Link data-testid="explore-collection-button" to={`/${product.category}`} className="w-full bg-ink text-white py-4 text-xs font-bold uppercase tracking-widest hover:bg-navy transition-colors flex items-center justify-center gap-2">
                Explore {catLabel} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/wholesale" className="w-full border border-ink py-4 text-xs font-bold uppercase tracking-widest hover:bg-ink hover:text-white transition-colors flex items-center justify-center gap-2">
                Wholesale Inquiries
              </Link>
            </div>

            <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
              <MapPin className="w-4 h-4 text-denim" /> AR / American Rag · Est. 1984
            </div>

            <Accordion type="single" collapsible className="mt-7">
              <AccordionItem value="details"><AccordionTrigger className="text-sm font-heading uppercase tracking-wide">Style Details</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">
                  Style <span className="text-ink font-medium">{product.styleCode}</span> · {product.garmentType}
                  {isDenim
                    ? <>. Available washes: {product.washes.join(", ")}.</>
                    : <>. Treatment: <span className="text-ink font-medium">{product.treatment}</span>.</>}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 lg:py-20 border-t border-border">
          <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight mb-8 text-center">More from {catLabel}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 sm:gap-6">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
